/**
 * Deteccao de tipo de arquivo por extensao e preview de conteudo.
 * Nao faz chamadas de rede. Nao persiste dados.
 */
import type { FileKind, LocalInputFile, SupportedEncoding } from './file.types';

export function detectFileKind(fileName: string, textPreview: string): FileKind {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';

  if (ext === 'afd') return 'afd';
  if (ext === 'aej') return 'aej';
  if (ext === 'csv') return 'complementary';

  if (ext === 'txt' || ext === 'unknown' || ext === '') {
    const preview = textPreview.slice(0, 500);
    const firstLine = preview.split('\n')[0]?.trim() ?? '';

    // AEJ pipe-delimited: first line starts with "01|" (header record type 01)
    if (firstLine.startsWith('01|')) {
      return 'aej';
    }

    // AEJ pipe-delimited: first line contains at least 5 pipe delimiters
    const pipeCount = (firstLine.match(/\|/g) ?? []).length;
    if (pipeCount >= 5) {
      return 'aej';
    }

    // AFD positional: sentinel trailer line "9999999990" anywhere in preview
    if (preview.includes('9999999990')) {
      return 'afd';
    }

    // AFD positional: header é o primeiro registro com NSR=000000001 e tipo=1
    // (Portaria 671 — NSR sequencial inicia em 1), portanto a linha começa com
    // 8 zeros seguidos de "11" (NSR=000000001 + tipo=1). Aceita também NSR=0
    // para tolerar geradores que indexam a partir de zero.
    if (/^0{8}[01]1/.test(firstLine) && firstLine.length >= 65) {
      return 'afd';
    }

    return 'unknown';
  }

  return 'unknown';
}

function detectEncoding(buffer: ArrayBuffer): SupportedEncoding {
  const bytes = new Uint8Array(buffer.slice(0, 3));
  // UTF-8 BOM: EF BB BF
  if (bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
    return 'utf-8';
  }
  // Try strict UTF-8 decoding
  try {
    new TextDecoder('utf-8', { fatal: true }).decode(buffer);
    return 'utf-8';
  } catch {
    return 'iso-8859-1';
  }
}

export async function readLocalFile(file: File): Promise<LocalInputFile> {
  const buffer = await file.arrayBuffer();
  const encoding = detectEncoding(buffer);
  const { decodeFileBuffer } = await import('./textDecoder');
  const text = decodeFileBuffer(buffer);
  const kind = detectFileKind(file.name, text.slice(0, 200));
  return {
    id: crypto.randomUUID(),
    name: file.name,
    sizeBytes: file.size,
    kind,
    text,
    encoding,
  };
}
